<template>
    <wrapper-features-templates-operations
    :applyErrorState="applyErrorState && !showLoader"
    :customBreadcrumbs="isCreatingMerchant ? {
        currentArea: t('companyPayments.baiPagaMerchant.creatingMerchant.breadcrumbs.currentArea'),
        parentMenus: [{
            id: 1,
            text: t('companyPayments.baiPagaMerchant.creatingMerchant.breadcrumbs.firstParent')
        }, {
            id: 2,
            text: t('companyPayments.baiPagaMerchant.creatingMerchant.breadcrumbs.secondParent'),
            internalClick: true
        }]
    } : null"
    :activeStep="activeStep"
    :feedbackOperationData="headerOperationData"
    :scrollValue="scrollValue"
    :stepperParams="stepperParams"
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :showStepper="activeStep <= stepperParams.length && isCreatingMerchant && !applyErrorState && !showLoader && !isAccountsEmpty"
    :showFooter="activeStep < 3 && isCreatingMerchant && !showLoader && !applyErrorState && !isAccountsEmpty"
    :footerButtonsParams="footerButtonsParams.filter(obj => !obj.hidden)"
    :showFloatingButton="isCreatingMerchant && activeStep === 1"
    :floatingButtonParams="{
        icon: 'help',
        label: t('companyPayments.baiPagaMerchant.creatingMerchant.helpBtn')
    }"
    :tokenTitleAppend="t('companyPayments.baiPagaMerchant.creatingMerchant.tokenTitleAppend')"
    @floatingButtonClicked="openHelperModal"
    @challengeSuccessHandler="challengeSuccessHandler"
    @challengeErrorHandler="challengeErrorHandler"
    @clearScrollToValue="scrollValue = null"
    @parentBreadcrumbClicked="resetForm"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- Insert content -->
        <component
        v-if="!showLoader"
        :is="getActiveStepComponent"
        :isMobile="getMobileAppState"
        :merchantInfo="merchantInfo"
        :clearFormErrors="clearFormErrors"
        :formData="formData"
        :mandatoryList="mandatoryList"
        :showMandatoryErrors="showMandatoryErrors"
        :responseData="responseData"
        @formDataChange="formDataChangeHandler"
        @updateCanProceed="formData.canProceed = !formData.canProceed"
        @updatetransactionsConditions="formData.transactionsConditions = !formData.transactionsConditions"
        @scrollTo="scrollValue = $event"
		@formErrorsCleared="clearFormErrors = false"
		@resetShowMandatoryErrorsProp="showMandatoryErrors = false"
        @goToCreatingMerchant="[isCreatingMerchant = true, activeStep++]"
        @openHelperModal="openHelperModal($event)"/>
    </wrapper-features-templates-operations>
    <!-- Helper Modal -->
    <modal-side-helper
    v-if="showHelperModal"
    helperStepComponentsFolder="features/companyPayments/baiPagaMerchant/helperModalSteps"
    :titlesArray="helperModalTopics"
    :initialHelperStep="initialHelperStep"
    @hideMe="[showHelperModal = false, initialHelperStep = null]"/>
    <!-- Feedback Toasts -->
    <toasts-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import ToastsGeneric from '@/components/generic/toasts/Generic'
import ModalSideHelper from '@/components/generic/modals/side/helper/HelperModal'

/* Vue */
import { ref, computed, defineAsyncComponent } from 'vue'

/* Aux */
import { createDownloadGenericReceiptPdf } from '@/network/api/operations/operations.api.js'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { atLeastOneIsEmpty, clearObjectStrings } from '@/assets/scripts/objects'
import { emailValidator } from '@/assets/scripts/validators'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const { validateMobileMerchantStatus, createMobilePaymentMerchant } = mapActions(moduleMapper.COMPANY_PAYMENTS_BAI_PAGA_MERCHANT)
const { getMobileAppState } = mapGetters()

/* Show Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)

/* Data */
const applyErrorState = ref(false)

/*************************************************
*                                                *
*                    Steps Data                  *
*                                                *
*************************************************/
const activeStep = ref(0)
const isCreatingMerchant = ref(false)

const stepperParams = ref([
    { description: t('companyPayments.baiPagaMerchant.creatingMerchant.stepper.data') },
    { description: t('companyPayments.baiPagaMerchant.creatingMerchant.stepper.validation') }
])

const getActiveStepComponent = computed(() => {
    if (!isCreatingMerchant.value) {
        const merchantComponent = `Has${merchantInfo.value.hasMerchant ? '' : 'No'}Merchant`
        return defineAsyncComponent(() => import('@/components/features/companyPayments/baiPagaMerchant/' + merchantComponent))
    }

    const stepNumber = activeStep.value
    return defineAsyncComponent(() => import('@/components/features/companyPayments/baiPagaMerchant/Step' + stepNumber))
})

const goToStep = stepNumber => {
	activeStep.value = stepNumber
	scrollValue.value = 0
}

/**********************************************************************
*                           Helper Modal                              *
**********************************************************************/
const showHelperModal = ref(false)
const initialHelperStep = ref(null)
const helperModalTopics = ref([
    t('companyPayments.baiPagaMerchant.creatingMerchant.helpModal.topic1'),
    t('companyPayments.baiPagaMerchant.creatingMerchant.helpModal.topic2'),
    t('companyPayments.baiPagaMerchant.creatingMerchant.helpModal.topic3'),
    t('companyPayments.baiPagaMerchant.creatingMerchant.helpModal.topic4')
])

const openHelperModal = (step) => {
    initialHelperStep.value = step || null
    showHelperModal.value = true
}

/*************************************************
*                                                *
*                Form Logic Data                 *
*                                                *
*************************************************/
const scrollValue = ref(null)
const showMandatoryErrors = ref(false)
const clearFormErrors = ref(false)

const formData = ref({
    merchantUserName: '',
    merchantUserEmail: '',
    merchantName: '',
    merchantEmail: '',
    acceptancePointName: '',
    acceptancePointMsisdn: '',
    acceptancePointNotificationEnabled: null,
    canProceed: false,
    transactionsConditions: false
})

const mandatoryList = ref([
    'merchantUserName',
    'merchantUserEmail',
    'merchantName',
    'merchantEmail',
    'acceptancePointName',
    'acceptancePointMsisdn',
	'acceptancePointNotificationEnabled',
    'canProceed',
    'transactionsConditions'
])

const formDataChangeHandler = ({ field, inputValue }) => {
    formData.value[field] = inputValue
}

/*************************************************
*             Submit Transfer Logic              *
*************************************************/
const createMerchant = async () => {
	showOverlayLoader.value = true

	const payload = {
        merchantUser: {
            name: formData.value.merchantUserName,
            email: formData.value.merchantUserEmail,
            profileId: 1
        },
        merchant: {
            friendlyName: formData.value.merchantName,
            merchantName: formData.value.merchantName,
            email: formData.value.merchantEmail,
            captivesEnabled: false,
            amount: 0,
            captivesMargin: 0
        },
        acceptancePoints: [{
            name: formData.value.acceptancePointName,
            msisdn: formData.value.acceptancePointMsisdn,
            defaultPoint: true,
            notificationEnabled: formData.value.acceptancePointNotificationEnabled === 1
        }]
	}

	try {
        await createMobilePaymentMerchant(payload)
		showOverlayLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
		errorHandler(error.response.data, createMerchant)
	}
}

/*************************************************
 *                                                *
 *             Success/Error Handlers             *
 *                                                *
 *************************************************/
const headerOperationData = ref(null)
const responseData = ref()

const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const challengeSuccessHandler = data => {
    responseData.value = {
		...data.data,
		errorLevel: data.errorLevel,
		code: data.code
	}

    headerOperationData.value = {
        status: data.errorLevel,
        title: data.message,
        buttonsData: !data.data.hasReceipt ? null : {
            iconsColor: data.errorLevel === 'INFO' ? 'var(--positive)' : 'var(--warning)',
            emailAction: 'sendOperationEmailNotification',
            smsAction: 'sendOperationSMSNotification',
            sendPayload: { operationId: data.data.operationId },
            downloadUrl: createDownloadGenericReceiptPdf({ params: { operationID: data.data.operationId } }),
            operationId: data.data.operationId
        }
    }

    goToStep(3)
}

const challengeErrorHandler = response => {
	feedbackToastData.value = errorObjectConstructor(response, createMerchant, () => { goToStep(1) })

	showFeedbackToast.value = true
	showOverlayLoader.value = false
}

const errorHandler = (data, retryFn) => {
	feedbackToastData.value = errorObjectConstructor(data, retryFn)

    showLoader.value = false
    showOverlayLoader.value = false
	showFeedbackToast.value = true
}

/*************************************************
*               Footer/Buttons Logic             *
*************************************************/
const validateMandatoryFormValues = () => {
    if (!formData.value.canProceed ||
		!formData.value.transactionsConditions ||
		(!!formData.value.merchantUserEmail.length && !emailValidator(formData.value.merchantUserEmail)) ||
		(!!formData.value.merchantEmail.length && !emailValidator(formData.value.merchantEmail)) ||
		formData.value.merchantUserName.length < 5 ||
		formData.value.merchantName.length < 5 ||
		formData.value.acceptancePointName.length < 5 ||
		!(formData.value.acceptancePointMsisdn.length === 9 || formData.value.acceptancePointMsisdn.length === 14) ||
		!formData.value.acceptancePointNotificationEnabled
		) {
        showMandatoryErrors.value = true
        return
    }

    atLeastOneIsEmpty(formData.value, mandatoryList.value) ? showMandatoryErrors.value = true : goToStep(2)
}

/* Footer Buttons Data */
const footerButtonsParams = computed(() => {
    return [{
        label: t('companyPayments.baiPagaMerchant.creatingMerchant.footerButtons.clear'),
        buttonPosition: 'left',
        hidden: activeStep.value !== 1,
		callbackFunction: () => { clearFormData() }
    }, {
        label: t('companyPayments.baiPagaMerchant.creatingMerchant.footerButtons.goBack'),
        icon: 'arrow-left-full',
        callbackFunction: () => { activeStep.value-- },
        hidden: activeStep.value === 1
    }, {
        label: t('companyPayments.baiPagaMerchant.creatingMerchant.footerButtons.forward'),
        icon: 'arrow-right-full',
        iconPosition: 'right',
        buttonType: 'primary',
        hidden: activeStep.value === 2,
		callbackFunction: () => { validateMandatoryFormValues() }

    }, {
        label: t('companyPayments.baiPagaMerchant.creatingMerchant.footerButtons.validate'),
        icon: 'check-line',
        iconPosition: 'right',
        buttonType: 'primary',
        hidden: activeStep.value !== 2,
		callbackFunction: () => { createMerchant() }
    }]
})

const clearFormData = () => {
	clearFormErrors.value = true
	clearObjectStrings(formData.value)
    formData.value.acceptancePointNotificationEnabled = null
    formData.value.canProceed = false
    formData.value.transactionsConditions = false

    headerOperationData.value = null
	responseData.value = null
	showOverlayLoader.value = false
}

const resetForm = () => {
    clearFormData()
    goToStep(0)
    isCreatingMerchant.value = false
}
/*************************************************
*                                                *
*                Initial Requets                 *
*                                                *
*************************************************/
const merchantInfo = ref({})
const initialRequests = async () => {
    applyErrorState.value = false
	showLoader.value = true

    try {
        const temp = await validateMobileMerchantStatus()
        merchantInfo.value = temp.data.data
        showLoader.value = false
    } catch (error) {
        applyErrorState.value = true
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, initialRequests)
    }
}

initialRequests()
</script>

<style lang="scss" scoped>

</style>
